import { VoidCallback1 } from '@deqo/foundation';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useObserver } from '../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../Legacy';
import { Button, Prompt, Select } from '../../ui';
import { useResolve } from '../Container';
import { AccountModel } from '../account/AccountModel';
import { labelSorter } from '../dashboard/Models';
import { Copy, Spacer } from '../dashboard/elements/LayoutElements';
import { Popup, PopupProps } from '../popups/Popup';

type LocationItem = {
    key: string,
    label: string,
    parent?: LocationItem,
};

const locationItem = (key: string, label: string, parent?: LocationItem) => ({
    key,
    label: `${!!parent ? '└── ' : ''}${label}`,
    parent,
});

const useLocationItems = () => {
    const { workspaces } = useObserver(useResolve(AccountModel));
    // Teams first
    // Then private
    // Then public
    return useMemo(() => {
        const ws = [
            ...workspaces.filter(x => x.type === 'team').sort(labelSorter),
            ...workspaces.filter(x => x.type === 'user').sort(labelSorter),
            // ...workspaces.filter(x => x.type === 'default').sort(labelSorter),
        ];
        return ws.flatMap(w => {
            const p = locationItem(w.id, w.label, null);
            return [p, ...Array.from(w.folders).sort(labelSorter).map(f => locationItem(f.id, f.label, p))];
        });
    }, [workspaces]);
};

export const AccountLocationSelector: LFC<{ initial?: string, onSelect: VoidCallback1<LocationItem> }> = ({ initial, onSelect }) => {
    const items = useLocationItems();
    const [selected, setSelected] = useState<LocationItem>(null);

    const onChange = useCallback((x: any) => {
        setSelected(x);
        onSelect(x);
    }, [onSelect]);

    useEffect(() => {
        if (!initial) return;
        const next = items?.find(x => x.key === initial) ?? null;
        setSelected(next);
        onSelect(next);
    }, []);

    return <Select placeholder="Select a team/folder..." items={items} selected={selected} onChange={onChange} />;
};

export type SelectWorkspacePopupArgs = {
    label: string,
    primary: string,
    initial?: string,
};

export const selectWorkspacePopup = (args: SelectWorkspacePopupArgs) => ({ ui: SelectWorkspacePopup, args });

export const SelectWorkspacePopup: LFC<PopupProps> = ({ action, dismiss, args }) => {
    const { label, primary, initial } = args as SelectWorkspacePopupArgs;

    const [to, setTo] = useState(null as LocationItem);
    const invalid = !to;

    const onApply = useCallback(() => {
        if (invalid) return;
        const workspaceId = to.key.startsWith('WO') ? to.key : to.parent.key;
        const folderId = to.key.startsWith('FO') ? to.key : null;
        action({ workspaceId, folderId });
    }, [to]);

    const onPromptAction = useCallback((confirm: boolean) => {
        if (!confirm) dismiss();
        else if (!invalid) onApply();
    }, [invalid, onApply]);

    return <Prompt onAction={onPromptAction}>
        <Popup w={32}>
            <Popup.Close onAction={() => onPromptAction(false)} />
            <Popup.Title>Select Location</Popup.Title>
            <Popup.Content>
                <Copy>
                    {label}
                </Copy>
                <Spacer />
                <AccountLocationSelector initial={initial} onSelect={setTo} />
                <Spacer />
            </Popup.Content>
            <Popup.Actions>
                <Button disabled={invalid} variant="primary" onAction={onApply}>
                    {primary}
                </Button>
                <Button onAction={dismiss}>
                    Cancel
                </Button>
            </Popup.Actions>
        </Popup>
    </Prompt>;
};
